import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    Box, Card, CardContent, Typography, Grid, Chip, Button, Table, TableHead,
    TableRow, TableCell, TableBody, TablePagination, Alert, CircularProgress,
    Tooltip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
    List, ListItem, ListItemText, ListItemButton, Divider, TextField, FormControl, InputLabel, Select, MenuItem,
    Checkbox, FormControlLabel
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartTooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import api from '../services/api';

const formatCurrency = (v) => v ? `R$ ${parseFloat(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '-';

const StatBox = ({ label, value, color, tooltip }) => (
    <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: `${color}10`, border: `1px solid ${color}30` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>{label}</Typography>
            {tooltip && <Tooltip title={tooltip}><InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} /></Tooltip>}
        </Box>
        <Typography variant="h6" fontWeight={700} sx={{ color }}>{value}</Typography>
    </Box>
);

const REGIONS = ['AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT', 'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO'];

const ResultsPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const isMaterial = searchParams.get('itemType') === 'material';
    const codeLabel = isMaterial ? 'CATMAT' : 'CATSER';

    // Filter states
    const [filters, setFilters] = useState(() => {
        const today = new Date();
        const past = new Date();
        past.setDate(today.getDate() - 180);
        const fmt = d => d.toISOString().split('T')[0];

        return {
            localFilter: '', // Starts empty, distinct from search query
            catmatCode: searchParams.get('catmatCode') || '',
            itemType: searchParams.get('itemType') || 'material',
            startDate: searchParams.get('startDate') || fmt(past),
            endDate: searchParams.get('endDate') || fmt(today),
            region: searchParams.get('region') || '',
            unit: searchParams.get('unit') || '',
            agency: searchParams.get('agency') || '',
            broadSearch: searchParams.get('broadSearch') === 'true'
        };
    });

    // List selection states
    const [openAddList, setOpenAddList] = useState(false);
    const [selectedItemToAdd, setSelectedItemToAdd] = useState(null);
    const [userLists, setUserLists] = useState([]);
    const [loadingLists, setLoadingLists] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');
    const [currentList, setCurrentList] = useState(null); // For scenario 2
    const [selectedList, setSelectedList] = useState(null); // For scenario 1 item selection
    const [submitting, setSubmitting] = useState(false);

    const query = searchParams.get('query');
    const catmatCode = searchParams.get('catmatCode');

    const doSearch = useCallback(async (currentParams) => {
        setLoading(true); setError('');
        try {
            const res = await api.get('/search', { params: currentParams });
            setData(res.data.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Erro ao realizar pesquisa. Verifique sua conexão.');
        } finally { setLoading(false); }
    }, []);

    const searchTrigger = `${catmatCode || query}-${searchParams.get('broadSearch')}`;

    useEffect(() => {
        const p = Object.fromEntries(searchParams.entries());
        if (p.query || p.catmatCode) doSearch(p);

        // If listId is present, fetch that specific list to know existing quotes
        const listId = p.listId;
        if (listId) {
            api.get(`/lists/${listId}`).then(res => setCurrentList(res.data.data)).catch(console.error);
        }
    }, [searchTrigger, doSearch]); // Only re-fetch if identity changes

    const handleFilterChange = (field) => (e) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        const newFilters = { ...filters, [field]: value };
        setFilters(newFilters);

        const newParams = new URLSearchParams();

        // Always preserve core search params from URL
        if (query) newParams.set('query', query);
        if (catmatCode) newParams.set('catmatCode', catmatCode);

        // Add other filters
        Object.entries(newFilters).forEach(([k, v]) => {
            if (v !== undefined && v !== '' && v !== false) newParams.set(k, v);
        });
        setSearchParams(newParams);
    };

    const handleExportPDF = async () => {
        try {
            const qs = searchParams.toString();
            const response = await api.get(`/reports/search?${qs}`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `pesquisa-precos-${Date.now()}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            setError('Erro ao gerar relatório PDF. Tente novamente.');
        }
    };

    const fetchLists = async () => {
        setLoadingLists(true);
        try {
            const res = await api.get('/lists');
            setUserLists(res.data.data);
        } catch (err) {
            console.error('Erro ao buscar listas', err);
        } finally {
            setLoadingLists(false);
        }
    };

    const handleOpenAddDialog = (item) => {
        setSelectedItemToAdd(item);
        const listId = searchParams.get('listId');
        const itemId = searchParams.get('itemId');

        if (listId && itemId) {
            // Scenario 2: Direct addition
            handleAddToList(listId, null, item);
        } else {
            // Scenario 1: Open dialog
            setOpenAddList(true);
            fetchLists();
        }
    };

    const handleAddToList = async (listId, manualItemId = null, itemObj = null) => {
        const itemId = manualItemId || searchParams.get('itemId');
        const item = itemObj || selectedItemToAdd;
        if (!item || submitting) return;

        setSubmitting(true);
        setError('');
        try {
            if (itemId) {
                // Adding quote to existing item
                await api.post(`/lists/${listId}/items/${itemId}/quotes`, {
                    description: item.description,
                    price: item.price,
                    unit: item.unit,
                    source: item.source,
                    date: item.date,
                    supplier: item.supplier,
                    agency: item.agency,
                    region: item.region,
                    externalId: item.id
                });
                setSuccessMsg(`Cotação adicionada ao item com sucesso!`);

                // Refresh current list if in context
                if (listId === searchParams.get('listId')) {
                    const res = await api.get(`/lists/${listId}`);
                    setCurrentList(res.data.data);
                }
            } else {
                // Adding new item
                await api.post(`/lists/${listId}/items`, {
                    description: item.description,
                    unit: item.unit,
                    quantity: 1,
                    estimatedPrice: item.price,
                    source: item.source,
                    date: item.date,
                    catmatCode: catmatCode
                });
                setSuccessMsg(`Item adicionado à lista com sucesso!`);
            }
            setOpenAddList(false);
            setSelectedList(null);
            setTimeout(() => setSuccessMsg(''), 3000);
        } catch (err) {
            setError(err.response?.data?.message || 'Erro ao adicionar item/cotação');
        } finally {
            setSubmitting(false);
        }
    };

    const items = useMemo(() => data?.items || [], [data]);

    const filteredItems = useMemo(() => items.filter(item => {
        const matchesQuery = !filters.localFilter ||
            item.description.toLowerCase().includes(filters.localFilter.toLowerCase()) ||
            (item.agency || '').toLowerCase().includes(filters.localFilter.toLowerCase()) ||
            (item.supplier || '').toLowerCase().includes(filters.localFilter.toLowerCase());

        const matchesUnit = !filters.unit || item.unit === filters.unit;
        const matchesRegion = !filters.region || item.region === filters.region;
        const matchesAgency = !filters.agency || (item.agency || '').toLowerCase().includes(filters.agency.toLowerCase());

        const itemDate = item.date ? new Date(item.date) : null;
        const matchesStartDate = !filters.startDate || (itemDate && itemDate >= new Date(filters.startDate));
        const matchesEndDate = !filters.endDate || (itemDate && itemDate <= new Date(filters.endDate));

        return matchesQuery && matchesUnit && matchesRegion && matchesAgency && matchesStartDate && matchesEndDate;
    }), [items, filters]);

    const stats = useMemo(() => {
        if (!filteredItems || filteredItems.length === 0) return { count: 0, mean: 0, median: 0, min: 0, max: 0, referencePrice: 0 };

        const prices = filteredItems.map(i => i.price).filter(p => p > 0).sort((a, b) => a - b);
        if (prices.length === 0) return { count: 0, mean: 0, median: 0, min: 0, max: 0, referencePrice: 0 };

        const count = prices.length;
        const mean = prices.reduce((sum, p) => sum + p, 0) / count;
        const median = count % 2 === 0 ? (prices[count / 2 - 1] + prices[count / 2]) / 2 : prices[Math.floor(count / 2)];

        const q1 = prices[Math.floor(count * 0.25)];
        const q3 = prices[Math.floor(count * 0.75)];
        const iqr = q3 - q1;
        const valid = prices.filter(p => p >= (q1 - 1.5 * iqr) && p <= (q3 + 1.5 * iqr));
        const referencePrice = valid.length > 0 ? (valid.length % 2 === 0 ? (valid[Math.floor(valid.length / 2) - 1] + valid[Math.floor(valid.length / 2)]) / 2 : valid[Math.floor(valid.length / 2)]) : median;

        return {
            count,
            mean,
            median,
            min: prices[0],
            max: prices[prices.length - 1],
            referencePrice,
            validCount: valid.length,
            outlierCount: count - valid.length
        };
    }, [filteredItems]);

    const sources = useMemo(() => [...new Set(filteredItems.map(i => i.source))], [filteredItems]);

    const chartData = useMemo(() => filteredItems.slice(0, 20).map((item, i) => ({
        name: `#${i + 1}`,
        price: item.price,
        source: item.source
    })), [filteredItems]);

    const uniqueResultUnits = useMemo(() => [...new Set((data?.items || []).map(i => i.unit))].filter(u => u).sort(), [data]);

    const renderUnit = (unit, qty) => {
        const isCollective = /CX|CAIXA|PCT|PACOTE|FARDO|MILHEIRO|LOTE|BD|GL/i.test(unit || '');
        return (
            <Box>
                <Chip
                    label={unit || 'UN'}
                    size="small"
                    sx={{
                        fontWeight: 800,
                        fontSize: '0.65rem',
                        height: 20,
                        bgcolor: isCollective ? '#fff3e0' : '#e8f5e9',
                        color: isCollective ? '#e65100' : '#2e7d32',
                        border: '1px solid currentColor',
                        mb: 0.5
                    }}
                />
                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                    {qty?.toLocaleString('pt-BR')} {unit}
                </Typography>
            </Box>
        );
    };

    if (!query && !catmatCode && !searchParams.get('itemId')) return <Alert severity="warning">Nenhuma pesquisa foi selecionada. <Button onClick={() => navigate('/pesquisa')}>Nova pesquisa</Button></Alert>;

    if (loading) return (
        <Box sx={{ textAlign: 'center', py: 15 }}>
            <CircularProgress size={80} thickness={4} color="primary" />
            <Typography variant="h6" sx={{ mt: 3, fontWeight: 700 }} color="primary">Buscando preços nos portais oficiais...</Typography>
            <Typography variant="body2" color="text.secondary">Processando dados do PNCP, Governamental, Atas, Contratos, Registro de Preços...</Typography>
        </Box>
    );

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1600, mx: 'auto' }}>
            {/* Header section */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={() => navigate('/pesquisa')} color="primary" sx={{ bgcolor: 'rgba(7, 73, 96, 0.1)', '&:hover': { bgcolor: 'rgba(7, 73, 96, 0.2)' } }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography variant="h4" fontWeight={900} color="primary">Resultados de Mercado</Typography>
                        <Typography variant="subtitle1" color="text.secondary">
                            {query ? `Busca: "${query}"` : (catmatCode ? `${codeLabel}: ${catmatCode}` : 'Relatório Consolidado')}
                        </Typography>
                    </Box>
                </Box>
                <Button variant="contained" size="large" startIcon={<PictureAsPdfIcon />} onClick={handleExportPDF} color="secondary" sx={{ borderRadius: 2, fontWeight: 700, px: 4 }}>
                    Relatório de Auditoria (PDF)
                </Button>
            </Box>

            {/* Filter Bar */}
            <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', border: '1px solid #eee' }}>
                <CardContent sx={{ py: 3, px: 3 }}>
                    <Grid container spacing={2} alignItems="flex-end">
                        <Grid item xs={12} sm={3}>
                            <TextField label="Filtrar resultados" size="small" variant="outlined" placeholder="Pesquise por palavras chave" fullWidth value={filters.localFilter} onChange={handleFilterChange('localFilter')} />
                        </Grid>
                        <Grid item xs={6} sm={1.5}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Unidade</InputLabel>
                                <Select sx={{ minWidth: 150 }} value={filters.unit} onChange={handleFilterChange('unit')} label="Unidade">
                                    <MenuItem value="">Todas</MenuItem>
                                    {uniqueResultUnits.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} sm={1.5}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Estado (UF)</InputLabel>
                                <Select sx={{ minWidth: 150 }} value={filters.region} onChange={handleFilterChange('region')} label="Estado (UF)">
                                    <MenuItem value="">Todos</MenuItem>
                                    {REGIONS.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} sm={2}>
                            <TextField label="Data Inicial" type="date" size="small" value={filters.startDate} onChange={handleFilterChange('startDate')} fullWidth InputLabelProps={{ shrink: true }} />
                        </Grid>
                        <Grid item xs={6} sm={2}>
                            <TextField label="Data Final" type="date" size="small" value={filters.endDate} onChange={handleFilterChange('endDate')} fullWidth InputLabelProps={{ shrink: true }} />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <TextField label="Órgão / UASG" size="small" value={filters.agency} onChange={handleFilterChange('agency')} fullWidth placeholder="Ex: 160001" />
                        </Grid>
                    </Grid>
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'row' }}>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, mr: 1, color: 'text.secondary', display: 'flex', alignItems: 'center' }}>INCLUIR:</Typography>
                        </Box>
                        {isMaterial && <>
                            <Grid item xs={12} sm={2}>
                                <FormControlLabel
                                    control={<Checkbox checked={filters.broadSearch} onChange={handleFilterChange('broadSearch')} color="primary" />}
                                    label={<Typography variant="body2" fontWeight={700}>Fontes de domínio amplo</Typography>}
                                />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <FormControlLabel
                                    control={<Checkbox checked={filters.cmedTables} onChange={handleFilterChange('cmedTables')} color="primary" />}
                                    label={<Typography variant="body2" fontWeight={700}>Tabelas do CMED</Typography>}
                                />
                            </Grid>
                        </>}
                        <Grid item xs={12} sm={2}>
                            <FormControlLabel
                                control={<Checkbox checked={filters.sinapiTables} onChange={handleFilterChange('sinapiTables')} color="primary" />}
                                label={<Typography variant="body2" fontWeight={700}>Tabelas do SINAPI</Typography>}
                            />
                        </Grid>
                    </Box>
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, mr: 1, color: 'text.secondary', display: 'flex', alignItems: 'center' }}>FONTES ATIVAS:</Typography>
                            {sources.map(s => <Chip key={s} label={s} size="small" sx={{ fontWeight: 800, bgcolor: s.includes('PNCP') ? '#e3f2fd' : '#f1f8e9', height: 20, fontSize: '0.65rem' }} />)}
                        </Box>
                        <Tooltip title="Ajuste os filtros acima para refinar os resultados e a média">
                            <Chip label="Filtros Ativos" icon={<TuneIcon />} variant="outlined" size="small" color="primary" sx={{ fontWeight: 700 }} />
                        </Tooltip>
                    </Box>
                </CardContent>
            </Card>

            {successMsg && <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>{successMsg}</Alert>}
            {error && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => navigate(0)}>Recarregar</Button>}>{error}</Alert>}

            <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <Chip label={`${codeLabel}: ${catmatCode || 'Personalizado'}`} color="primary" sx={{ fontWeight: 800 }} />
                    <Chip label={query || 'Pesquisa Direta'} variant="outlined" sx={{ fontWeight: 700 }} />
                </Box>

                {/* Stats cards */}
                <Grid container spacing={2} sx={{ mb: 4 }}>
                    <Grid item xs={6} md={2}><StatBox label="Cotações" value={stats.count || 0} color="#6b7a90" /></Grid>
                    <Grid item xs={6} md={2}><StatBox label="Média" value={formatCurrency(stats.mean)} color="#0288d1" /></Grid>
                    <Grid item xs={6} md={2}><StatBox label="Mediana" value={formatCurrency(stats.median)} color="#ed6c02" /></Grid>
                    <Grid item xs={6} md={2}><StatBox label="Mínimo" value={formatCurrency(stats.min)} color="#2e7d32" /></Grid>
                    <Grid item xs={6} md={2}><StatBox label="Máximo" value={formatCurrency(stats.max)} color="#d32f2f" /></Grid>
                    <Grid item xs={12} md={2}><StatBox label="Referência Radar" value={formatCurrency(stats.referencePrice)} color="#2b579e" tooltip="Valor pós-limpeza IQR" /></Grid>
                </Grid>

                {/* Table */}
                <Card sx={{ mb: 3, overflow: 'hidden' }}>
                    <Table size="small">
                        <TableHead sx={{ bgcolor: 'primary.main' }}>
                            <TableRow>
                                <TableCell sx={{ color: 'white', fontWeight: 700 }}>Descrição Detalhada</TableCell>
                                <TableCell align="center" sx={{ color: 'white', fontWeight: 700 }}>Órgão / UF</TableCell>
                                <TableCell align="center" sx={{ color: 'white', fontWeight: 700 }}>Preço Unitário</TableCell>
                                <TableCell align="center" sx={{ color: 'white', fontWeight: 700 }}>Unid. / Qtd.</TableCell>
                                <TableCell align="center" sx={{ color: 'white', fontWeight: 700 }}>Data</TableCell>
                                <TableCell align="center" sx={{ color: 'white', fontWeight: 700 }}>Fonte</TableCell>
                                <TableCell align="center" sx={{ color: 'white', fontWeight: 700 }}>Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredItems.slice(page * rowsPerPage, (page + 1) * rowsPerPage).map((item, i) => (
                                <TableRow key={i} hover>
                                    <TableCell sx={{ maxWidth: 300 }}>
                                        <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>{item.description}</Typography>
                                        <Typography variant="caption" color="text.secondary" display="block">{item.supplier}</Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{item.agency}</Typography>
                                        <Chip label={item.region || 'BR'} size="small" sx={{ height: 18, fontSize: '0.7rem', fontWeight: 700, bgcolor: '#f0f0f0' }} />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Typography variant="h6" color="primary" fontWeight={800} sx={{ fontSize: '1rem' }}>{formatCurrency(item.price)}</Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        {renderUnit(item.unit, item.quantity)}
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontSize: '0.8rem' }}>{item.date ? new Date(item.date).toLocaleDateString('pt-BR') : '-'}</TableCell>
                                    <TableCell align="center">
                                        <Chip label={item.source} size="small" sx={{ fontSize: '0.65rem' }} />
                                    </TableCell>
                                    <TableCell align="center">
                                        {(() => {
                                            const listId = searchParams.get('listId');
                                            const itemId = searchParams.get('itemId');
                                            const isAlreadyAdded = currentList?.items?.find(it => it._id === itemId)?.quotes?.some(q => q.externalId === item.id);

                                            if (listId && itemId && isAlreadyAdded) {
                                                return <Chip icon={<CheckCircleIcon />} label="Adicionado" color="success" size="small" variant="outlined" />;
                                            }

                                            return (
                                                <Button
                                                    variant={listId && itemId ? "contained" : "outlined"}
                                                    size="small"
                                                    disabled={submitting}
                                                    onClick={() => handleOpenAddDialog(item)}
                                                    startIcon={listId && itemId ? <CheckCircleIcon /> : <AddShoppingCartIcon sx={{ fontSize: '1rem !important' }} />}
                                                >
                                                    {listId && itemId ? 'Incluir' : 'Add'}
                                                </Button>
                                            );
                                        })()}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div" count={filteredItems.length} page={page}
                        onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
                        rowsPerPageOptions={[10, 25, 50]}
                        labelRowsPerPage="Exibir:"
                    />
                </Card>


                {/* Chart */}
                <Card sx={{ mb: 3, height: '100%', width: '100%' }}>
                    <CardContent>
                        <Typography variant="subtitle1" fontWeight={700} mb={2}>Análise de Dispersão</Typography>
                        <ResponsiveContainer width="100%" height={250}>
                            <BarChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="name" fontSize={10} />
                                <YAxis tickFormatter={(val) => `R$${val}`} fontSize={10} />
                                <RechartTooltip formatter={(v) => [formatCurrency(v), 'Preço']} />
                                <ReferenceLine y={stats.referencePrice} stroke="#2b579e" strokeDasharray="3 3" label={{ value: 'Referência', position: 'right', fill: '#2b579e', fontSize: 10 }} />
                                <Bar dataKey="price" fill="#2b579e" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>

                {/* Resumo Metodológico */}
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="subtitle1" fontWeight={700} mb={1}>Resumo Metodológico</Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                            Os preços apresentados são provenientes de licitações públicas homologadas e atas vigentes. O sistema aplica o método IQR (Intervalo Interquartil) para remover outliers (preços excessivamente altos ou baixos).
                        </Typography>
                        <Box sx={{ p: 2, bgcolor: '#f0f4f8', borderRadius: 1 }}>
                            <Typography variant="caption" display="block">Total Bruto: {stats.count}</Typography>
                            <Typography variant="caption" display="block">Outliers Removidos: {stats.outlierCount}</Typography>
                            <Typography variant="caption" display="block">Base de Cálculo: {stats.validCount} itens</Typography>
                        </Box>
                    </CardContent>
                </Card>
            </>

            {/* Dialog Add to List */}
            <Dialog open={openAddList} onClose={() => { setOpenAddList(false); setSelectedList(null); }} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontWeight: 700 }}>{selectedList ? `Adicionar a: ${selectedList.name}` : 'Adicionar à Lista de Compras'}</DialogTitle>
                <DialogContent>
                    {!selectedList && <Typography variant="body2" sx={{ mb: 2 }}>Escolha a lista ou projeto onde deseja salvar esta cotação:</Typography>}
                    {loadingLists ? (
                        <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={30} /></Box>
                    ) : userLists.length === 0 ? (
                        <Alert severity="info" action={<Button color="inherit" size="small" onClick={() => navigate('/listas')}>Criar Lista</Button>}>Você ainda não possui listas.</Alert>
                    ) : (
                        <List sx={{ border: '1px solid #eee', borderRadius: 1 }}>
                            {!selectedList ? (
                                userLists.map((list) => (
                                    <ListItem key={list._id} disablePadding divider>
                                        <ListItemButton onClick={() => setSelectedList(list)}>
                                            <ListItemText primary={list.name} secondary={`${list.items?.length || 0} itens • Criada em: ${new Date(list.createdAt).toLocaleDateString('pt-BR')}`} />
                                            <ArrowBackIcon sx={{ transform: 'rotate(180deg)', fontSize: 'small', opacity: 0.5 }} />
                                        </ListItemButton>
                                    </ListItem>
                                ))
                            ) : (
                                <>
                                    <ListItem disablePadding divider>
                                        <ListItemButton onClick={() => handleAddToList(selectedList._id, null, selectedItemToAdd)} disabled={submitting}>
                                            <ListItemText primary="Adicionar como NOVO ITEM" primaryTypographyProps={{ color: 'primary', fontWeight: 700 }} />
                                        </ListItemButton>
                                    </ListItem>
                                    <Divider><Chip label="Ou adicionar cotação a item existente" size="small" /></Divider>
                                    {selectedList.items.map((it) => (
                                        <ListItem key={it._id} disablePadding divider>
                                            <ListItemButton onClick={() => handleAddToList(selectedList._id, it._id, selectedItemToAdd)} disabled={submitting}>
                                                <ListItemText primary={it.description} secondary={`${it.quotes?.length || 0} cotações existentes`} />
                                                <AddShoppingCartIcon color="action" fontSize="small" />
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                                </>
                            )}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setOpenAddList(false); setSelectedList(null); }}>Cancelar</Button>
                    {selectedList && <Button onClick={() => setSelectedList(null)}>Voltar para Listas</Button>}
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ResultsPage;
